// Partita.tsx — dentro la stanza: i due video, il conto alla rovescia, «Ha riso!», accuse, pause, risultato, festa.
// Mostra solo quello che il server decide (la "vista"); le azioni del giocatore diventano messaggi al server.
import { vibra as tocco } from "../servizi/nativo.ts";
import { IconaEsci, IconaRisata, IconaSegnala, IconaAltoparlante } from "../componenti/IconeGioco.tsx";
import { Tutorial, tutorialVisto, type TipoTutorial } from "../componenti/Tutorial.tsx";
import { conVoce, useVoce } from "./usaVoce.ts";
import { IconaVoce, SceltaVoce } from "./SceltaVoce.tsx";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Finestra } from "../componenti/Finestra.tsx";
import { Segnala } from "./Segnala.tsx";
import { indirizzoSito, linkSfida } from "./Condividi.tsx";
import { InvitoSfida, PostoAmico } from "./InvitoSfida.tsx";
import { stanza, useStanza } from "./stanza.ts";
import { Videochiamata, type StatoVideo } from "./video.ts";
import { Trasmissioni } from "./spettatori.ts";
import { creaVideoBot, type StatoBot, type VideoBot } from "../prova/videoBot.ts";
import { PannelloProva } from "../prova/PannelloProva.tsx";
import { opzioneProva } from "../servizi/prova.ts";
import { clipPossibili, useClip } from "./usaClip.ts";
import { tokenTorneo, torneoDellaPartita, statoTorneo, vinciATavolino } from "../servizi/tornei.ts";
import { ErroreRete } from "../servizi/api.ts";
import { avviaAnalisi, preparaRilevamento, type Analisi } from "./rilevamento.ts";
import { useFiltri } from "./usaFiltri.ts";
import { collegaSfida, nessunoAbbonato, useAccount } from "../servizi/account.ts";
import { vai } from "../servizi/percorso.ts";
import { SceltaFiltro } from "./SceltaFiltro.tsx";
import { Annuncio, AnnuncioRisultato } from "./AnnuncioRisultato.tsx";
import { AmiciziaFinePartita, FinestraFinePartita, Riepilogo } from "./FinePartita.tsx";
import { caricaVideoNoi } from "../servizi/video.ts";
import { domandaDaFare, noiPossibileQui, scelteAttuali } from "../servizi/scelte.ts";
import { DomandaScelte } from "../componenti/DomandaScelte.tsx";
import { useFotoPartita } from "./fotoPartita.ts";
import { PulsanteBot } from "../prova/PulsanteBot.tsx";
import { creaPartitaProva } from "../servizi/prova.ts";
import { leggiRegoleStelle } from "../servizi/social.ts";
import { registraGiorno } from "../servizi/giorniSalvati.ts";
import { momentoRotazione, prossimoFiltro } from "./rotazione.ts";
import { coriandoli, sbloccaAudio, suono, vibra, suoniAttivi } from "./festa.ts";
import { tempoReale } from "../servizi/tempoReale.ts";
import { leggiSfida, salvaToken, serverIce, sfidaPer } from "../servizi/sfide.ts";
import { eNumero, leggiImpostazione } from "../servizi/impostazioni.ts";
import { leggiPreferenza, salvaPreferenza } from "../servizi/preferenze.ts";
import { IMP_APP, PERCORSO_PIANI, PERCORSO_SFIDA, PERCORSO_TORNEO, PREFERENZE } from "../costanti.ts";
import { altro, type Ruolo, type VistaPartita } from "../../../condiviso/partita.ts";
import type { DatiSegnale } from "../../../condiviso/protocollo.ts";

export const DISPOSIZIONI = ["chiamata", "sopra_sotto", "affiancati"] as const;
export type Disposizione = (typeof DISPOSIZIONI)[number];
const AGGIORNA_OROLOGIO_MS = 100;

interface Props {
  codice: string;
  flusso: MediaStream;
  onEsci: () => void;
  onNuovaSfida: () => void;
}

/** Ora del server che avanza da sola mentre serve (conto alla rovescia, tempo di gioco, scadenze). */
export function useOraServer(attivo: boolean): number {
  const [ora, setOra] = useState(() => tempoReale.oraServer());
  useEffect(() => {
    if (!attivo) return;
    const id = setInterval(() => setOra(tempoReale.oraServer()), AGGIORNA_OROLOGIO_MS);
    return () => clearInterval(id);
  }, [attivo]);
  // v035: l'ora si legge sempre adesso (lo stato serve solo a ridisegnare). Prima, appena si attivava, per un attimo
  // valeva l'ora di quando la pagina era stata aperta: il conto mostrava «13» invece di «5».
  void ora;
  return tempoReale.oraServer();
}

const secondi = (ms: number) => Math.max(0, Math.ceil(ms / 1000));
/** v035: dopo quanto si parte da soli quando vi vedete (il tempo di accorgersene), e tra un round e l'altro. */
const PARTI_DA_SOLO_MS = 1200;
const RIPARTI_ROUND_MS = 4000;
/** v035: dopo quanto si apre «Che partita!» (prima si vede il risultato sui video). */
const APRI_FINE_MS = 1500;
const mmss = (ms: number) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

export function Partita({ codice, flusso, onEsci, onNuovaSfida }: Props) {
  const { t, i18n } = useTranslation();
  const st = useStanza();
  const v = st.vista;
  const io: Ruolo = st.ruolo ?? "a";
  const lui = altro(io);
  // v028: disposizione fissa (sopra e sotto sul telefono, affiancati su PC: lo decide il CSS); niente menu ⋯

  const [segnala, setSegnala] = useState(false);
  const [conferma, setConferma] = useState(false);
  const [statoVideo, setStatoVideo] = useState<StatoVideo>("collegamento");
  const [remoto, setRemoto] = useState<MediaStream | null>(null);
  const [rilevamento, setRilevamento] = useState<"caricamento" | "attivo" | "assente">("caricamento");
  const [voltoMio, setVoltoMio] = useState(true);
  const [suoni, setSuoni] = useState(suoniAttivi);
  const [punteggi, setPunteggi] = useState({ io: 0, avversario: 0 });
  const [sceltaFiltro, setSceltaFiltro] = useState(false);
  const [sceltaVoce, setSceltaVoce] = useState(false);
  // v046: i pulsanti «Cambia filtro» / «Cambia voce» pulsano finché non li si è mai toccati
  const [toccatoFiltro, setToccatoFiltro] = useState(() => leggiPreferenza(PREFERENZE.toccati)?.includes("filtro") ?? false);
  const [toccatoVoce, setToccatoVoce] = useState(() => leggiPreferenza(PREFERENZE.toccati)?.includes("voce") ?? false);
  const segnaToccato = (k: "filtro" | "voce") => {
    const ora = leggiPreferenza(PREFERENZE.toccati) ?? "";
    if (!ora.includes(k)) salvaPreferenza(PREFERENZE.toccati, `${ora} ${k}`.trim());
    if (k === "filtro") setToccatoFiltro(true);
    else setToccatoVoce(true);
  };
  const filtri = useFiltri(flusso);
  const videoIo = useRef<HTMLVideoElement>(null);
  const videoLui = useRef<HTMLVideoElement>(null);
  const tela = useRef<HTMLCanvasElement>(null);
  const analisi = useRef<Analisi | null>(null);
  const faseRif = useRef(v?.fase);
  faseRif.current = v?.fase;

  const fase = v?.fase ?? "attesa";
  // v019: l'amico non è ancora arrivato (e la sfida non è una richiesta dal link personale): schermata d'invito.
  const invito = fase === "attesa" && !!v && !v.collegati[lui] && !v.nomi[lui] && !sfidaPer(codice);
  const ora = useOraServer(["conto", "gioco", "accusa", "pausa", "verifica"].includes(fase));
  const nomeLui = v?.nomi[lui] || t("gioco.avversario");
  const presenteLui = v?.collegati[lui] ?? false;
  // v030: mini tutorial la prima volta (1v1 o torneo), quando l'altro è arrivato e prima di «Sono pronto»
  const tipoTutorial: TipoTutorial = torneoDellaPartita(codice) ? "torneo" : "1v1";
  const [tutorial, setTutorial] = useState(false);
  useEffect(() => {
    if (v && !invito && fase === "attesa" && !tutorialVisto(tipoTutorial)) setTutorial(true);
  }, [v, invito, fase, tipoTutorial]);

  // v035: niente «Sono pronto» da premere (i tester restavano fermi): appena vi vedete parte da solo il conto alla
  // rovescia. Tra i round di «al meglio di 3» si riparte da soli dopo RIPARTI_ROUND_MS. Il server resta uguale (vuole i
  // due «pronto»): li manda l'app. v042: tolto anche «Aspetta un attimo» (si parte e basta).
  const videoPronto = statoVideo === "collegato" || st.prova;
  const serieInCorso = fase === "risultato" && !!v?.esito && !v.esito.serieFinita && !v.esito.annullata;
  useEffect(() => {
    if (!v || tutorial || v.pronti[io] || !v.collegati[lui]) return;
    const attesa = fase === "attesa" && videoPronto ? PARTI_DA_SOLO_MS : serieInCorso ? RIPARTI_ROUND_MS : null;
    if (attesa === null) return;
    const id = setTimeout(() => {
      sbloccaAudio();
      stanza.pronto(true);
    }, attesa);
    return () => clearTimeout(id);
  }, [v, fase, io, lui, tutorial, videoPronto, serieInCorso]);

  // v035: a fine partita (serie finita) si apre da sola la finestra «Che partita!», una volta per serie, dopo un attimo
  // (si vede prima chi ha vinto).
  const [fineAperta, setFineAperta] = useState(false);
  // v045: dopo la primissima partita «Le tue scelte» (o, una volta per apertura, il promemoria), poi «Che partita!»
  const [domanda, setDomanda] = useState<"prima" | "promemoria" | null>(null);
  const fineVista = useRef<string | null>(null);
  const serieFinita = fase === "risultato" && !!v?.esito && v.esito.serieFinita && !v.esito.annullata;
  const chiaveSerie = v ? `${v.serie.numero}` : null;
  useEffect(() => {
    if (!serieFinita || !chiaveSerie || fineVista.current === chiaveSerie) return;
    fineVista.current = chiaveSerie;
    const id = setTimeout(() => {
      const d = domandaDaFare(clipPossibili());
      if (d) setDomanda(d);
      else setFineAperta(true);
    }, APRI_FINE_MS);
    return () => clearTimeout(id);
  }, [serieFinita, chiaveSerie]);
  useEffect(() => {
    if (fase !== "risultato") {
      setFineAperta(false);
      setDomanda(null);
    }
  }, [fase]);
  // l'altro propone di giocare ancora: la finestra si toglie, così si vede «Accetta / No grazie» nel pannello
  const propostaDaLui = !!v?.proposta && v.proposta.da === lui;

  // Lo sfidato registrato si fa riconoscere dal server (partite valide, niente pubblicità se abbonato).
  const account = useAccount();
  // Modalità di prova (v008): il tester può vedere i numeri del rilevamento e forzare il video via TURN.
  const tester = account.tipo === "registrato" && account.io.tester === true;
  const diagnostica = tester && opzioneProva(PREFERENZE.provaDiagnostica);
  const soloTurn = tester && opzioneProva(PREFERENZE.provaSoloTurn);
  // v039: con chi si è bloccato (in un senso o nell'altro) non si gioca: il server rifiuta e qui si esce.
  const [vietato, setVietato] = useState(false);
  useEffect(() => {
    let attivo = true;
    if (st.ruolo === "b" && st.token && account.tipo === "registrato")
      void collegaSfida(codice, st.token).catch((e: unknown) => {
        // v040: il server chiude anche la stanza («bloccato» a tutti e due): se è già arrivato, ci pensa Gioco
        if (!attivo || stanza.stato.passo === "errore") return;
        if (e instanceof ErroreRete && e.codice === "bloccato") {
          setVietato(true);
          // subito fuori dalla stanza e niente più video verso quella persona
          chiamataRif.current?.chiudi();
          stanza.esci();
        }
      });
    return () => {
      attivo = false;
    };
  }, [st.ruolo, st.token, account.tipo, codice]);

  // --- video locale -------------------------------------------------------------------
  // Con un filtro attivo (e già disegnato) si vede e si invia il video filtrato; altrimenti la fotocamera.
  const invio = filtri.motore && filtri.uscitaAttiva ? filtri.motore.uscita : flusso;
  const invioRif = useRef(invio);
  invioRif.current = invio;
  const chiamataRif = useRef<Videochiamata | null>(null);
  // v026: voce buffa (all'altro arriva la voce cambiata)
  const voce = useVoce(flusso);
  const voceRif = useRef(voce.traccia);
  voceRif.current = voce.traccia;
  useEffect(() => {
    if (voce.traccia) void chiamataRif.current?.sostituisciAudio(voce.traccia).catch(() => undefined);
  }, [voce.traccia]);
  const trasmissioniRif = useRef<Trasmissioni | null>(null);
  useEffect(() => {
    if (videoIo.current) videoIo.current.srcObject = invio;
    trasmissioniRif.current?.cambiaFlusso(invio);
    // Videochiamata già avviata: si cambia solo la traccia video, senza rinegoziare.
    const traccia = invio.getVideoTracks()[0];
    if (traccia) void chiamataRif.current?.sostituisciVideo(traccia).catch(() => undefined);
  }, [invio]);
  useEffect(() => {
    if (videoLui.current) videoLui.current.srcObject = remoto;
  }, [remoto]);

  // --- videochiamata --------------------------------------------------------------------------
  // Parte quando ci sono entrambi e si rifà SOLO quando il server dice che uno dei due si è ricollegato
  // (contatore delle sessioni): così la rifanno insieme tutti e due i telefoni. Se cade solo il canale del server,
  // la videochiamata in corso non si tocca (se cade davvero, ci pensa il riavvio ICE).
  const chiaveVideoRif = useRef<string | null>(null);
  if (v && presenteLui) chiaveVideoRif.current = `${v.sessioni.a}-${v.sessioni.b}`;
  const chiaveVideo = chiaveVideoRif.current;

  // Partita di prova (v008): l'avversario è il bot, il suo "video" è disegnato qui (niente videochiamata).
  const botRif = useRef<VideoBot | null>(null);
  useEffect(() => {
    if (st.passo !== "dentro" || !st.prova) return;
    const stile = getComputedStyle(document.documentElement);
    const colore = (n: string, r: string) => stile.getPropertyValue(n).trim() || r;
    const bot = creaVideoBot({ sfondo: colore("--c-superficie", "#fff6e0"), faccia: colore("--c-accento", "#ffd23f"), tratto: colore("--c-testo", "#1e1433") });
    botRif.current = bot;
    setRemoto(bot.flusso);
    setStatoVideo("collegato");
    return () => {
      bot.ferma();
      botRif.current = null;
      setRemoto(null);
    };
  }, [st.passo, st.prova]);
  const statoBot: StatoBot =
    !v?.collegati[lui] || v?.volti[lui] === false ? "assente" : v?.esito?.perdente === lui && (fase === "risultato" || fase === "verifica") ? "ride" : "neutro";
  useEffect(() => botRif.current?.imposta(statoBot), [statoBot]);

  useEffect(() => {
    if (st.passo !== "dentro" || chiaveVideo === null || !st.token || st.prova) return;
    let chiamata: Videochiamata | null = null;
    let chiuso = false;
    const inCoda: DatiSegnale[] = [];
    const stacca = stanza.suSegnale((d) => (chiamata ? void chiamata.ricevi(d) : inCoda.push(d)));
    void serverIce(codice, st.token)
      .catch(() => ({ iceServers: [], turn: false }))
      .then((r) => {
        if (chiuso) return;
        chiamata = new Videochiamata({
          soloTurn,
          iceServers: r.iceServers,
          educato: io === "b",
          locale: conVoce(invioRif.current, voceRif.current),
          inviaSegnale: (d) => stanza.segnale(d),
          suRemoto: setRemoto, // sempre la stessa MediaStream: il rilevamento non riparte a ogni traccia
          suStato: setStatoVideo,
        });
        chiamataRif.current = chiamata;
        for (const d of inCoda.splice(0)) void chiamata.ricevi(d);
      });
    return () => {
      chiuso = true;
      stacca();
      chiamata?.chiudi();
      chiamataRif.current = null;
      setRemoto(null);
    };
  }, [st.passo, st.token, chiaveVideo, codice, flusso, io, st.prova, soloTurn]);

  // --- torneo: il mio video a chi guarda la finale ----------------------------------------------
  // Solo il server fa entrare gli spettatori (finale di un torneo): qui si risponde a chi chiede il video.
  useEffect(() => {
    if (st.passo !== "dentro" || !st.token) return;
    const token = st.token;
    let ice: Promise<{ urls: string[]; username?: string; credential?: string }[]> | null = null;
    const tr = new Trasmissioni(
      invioRif.current,
      () =>
        (ice ??= serverIce(codice, token)
          .then((r) => r.iceServers)
          .catch(() => {
            ice = null; // la prossima offerta riprova a chiedere i server
            return [];
          })),
      (con, d) => stanza.segnaleSpettatore(con, d),
    );
    trasmissioniRif.current = tr;
    const stacca = stanza.suSegnaleSpettatore((da, d) => void tr.ricevi(da, d));
    return () => {
      stacca();
      tr.chiudi();
      trasmissioniRif.current = null;
    };
  }, [st.passo, st.token, codice]);
  useEffect(() => trasmissioniRif.current?.presenti(st.spettatori), [st.spettatori]);

  // --- rilevamento automatico ---------------------------------------------------------------
  // Il proprio volto: se c'è il motore dei filtri lo analizza già lui (stesso analizzatore, una volta sola);
  // per questo si aspetta di sapere se il motore c'è prima di partire.
  const motore = filtri.motore;
  const motoreDeciso = filtri.stato !== "attesa";
  useEffect(() => {
    if (!remoto || !motoreDeciso) return;
    let fermo = false;
    void preparaRilevamento().then((fl) => {
      if (fermo) return;
      if (!fl || !videoIo.current || !videoLui.current) {
        setRilevamento("assente");
        return;
      }
      setRilevamento("attivo");
      // L'analisi riparte supponendo il volto presente: lo si dice anche al server.
      setVoltoMio(true);
      stanza.volto(true);
      analisi.current = avviaAnalisi(fl, { io: motore ?? videoIo.current, avversario: videoLui.current }, () => tempoReale.oraServer(), (chi, e) => {
        if (e.tipo === "volto") {
          if (chi === "io") {
            setVoltoMio(e.presente);
            stanza.volto(e.presente);
          }
          return;
        }
        if (faseRif.current === "gioco" || faseRif.current === "verifica") stanza.risata(chi, e.t);
      });
    });
    const misura = setInterval(() => analisi.current && setPunteggi(analisi.current.punteggi()), 250);
    return () => {
      fermo = true;
      clearInterval(misura);
      analisi.current?.ferma();
      analisi.current = null;
    };
  }, [remoto, motore, motoreDeciso]);

  // Nuovo round: i rilevatori ripartono da zero (niente risate "in coda").
  useEffect(() => {
    if (fase === "conto") analisi.current?.azzera();
  }, [fase, v?.round]);

  // --- suoni del conto alla rovescia e del VIA ---------------------------------------------------
  const contoRestante = v?.tVia ? v.tVia - ora : 0;
  const numeroConto = fase === "conto" ? secondi(contoRestante) : 0;
  const ultimoNumero = useRef(0);
  useEffect(() => {
    if (fase === "conto" && numeroConto > 0 && numeroConto !== ultimoNumero.current) {
      suono("conto");
      tocco(numeroConto <= 3 ? "medio" : "leggero"); // v046: ogni numero si sente anche in mano
    }
    if (fase === "gioco" && ultimoNumero.current > 0) {
      suono("via");
      vibra("via");
    }
    ultimoNumero.current = fase === "conto" ? numeroConto : 0;
  }, [fase, numeroConto]);

  // --- v046: la tensione — quando qualcuno sta per ridere compare un fumetto e il telefono vibra piano -------------
  const [quasi, setQuasi] = useState<{ chi: "io" | "lui"; testo: string } | null>(null);
  const ultimoQuasi = useRef(0);
  const togliQuasi = useRef(0);
  useEffect(() => {
    if (fase !== "gioco" || rilevamento !== "attivo") return setQuasi(null);
    const soglia = leggiImpostazione(IMP_APP.rilevamentoSoglia, 0.45, eNumero) * 0.65;
    const chi = punteggi.avversario >= soglia ? "lui" : punteggi.io >= soglia ? "io" : null;
    if (!chi || Date.now() - ultimoQuasi.current < 4000) return;
    ultimoQuasi.current = Date.now();
    tocco("leggero");
    const n = 1 + Math.floor(Math.random() * 3);
    setQuasi({ chi, testo: chi === "lui" ? t(`gioco.quasi_lui_${n}`) : t(`gioco.quasi_io_${n}`) });
    window.clearTimeout(togliQuasi.current);
    togliQuasi.current = window.setTimeout(() => setQuasi(null), 1800);
  }, [fase, rilevamento, punteggi, t]);
  useEffect(() => () => window.clearTimeout(togliQuasi.current), []);

  // --- festa alla fine ------------------------------------------------------------------------
  const esito = v?.fase === "risultato" ? v.esito : null;
  // Una festa per round giocato (serie + round), non a ogni aggiornamento della vista.
  const chiaveEsito = esito && v ? `${v.serie.numero}-${esito.round}-${esito.motivo}` : null;
  const festeggiato = useRef<string | null>(null);
  const fermaCoriandoli = useRef<(() => void) | null>(null);
  useEffect(() => () => fermaCoriandoli.current?.(), []);
  useEffect(() => {
    if (!esito || !chiaveEsito || festeggiato.current === chiaveEsito) return;
    festeggiato.current = chiaveEsito;
    if (esito.vincitore === io) {
      suono("vittoria");
      vibra("vittoria");
      const stile = getComputedStyle(document.documentElement);
      const colori = ["--c-primario", "--c-accento", "--c-secondario", "--c-successo", "--c-sfondo"].map((c) => stile.getPropertyValue(c).trim()).filter(Boolean);
      const durata = leggiImpostazione(IMP_APP.festaSecondi, 4, eNumero) * 1000;
      fermaCoriandoli.current?.();
      if (tela.current) fermaCoriandoli.current = coriandoli(tela.current, colori, durata);
    } else if (esito.vincitore === lui) {
      suono("sconfitta");
      vibra("sconfitta");
    }
  }, [chiaveEsito, esito, io, lui]);

  // --- v020: il fotogramma vero del momento della risata ----------------------------------------
  // Quando il gioco si ferma (risata riconosciuta o «Ha riso!») si fotografano i due video. La foto resta solo nella
  // memoria di questa pagina: non va al server e non si salva (si vede in «Hai vinto / Hai riso» e sparisce al round dopo).
  const [fotoRisata, setFotoRisata] = useState<{ io: string | null; lui: string | null } | null>(null);
  const fasePrima = useRef(fase);
  useEffect(() => {
    const prima = fasePrima.current;
    fasePrima.current = fase;
    if (prima === "gioco" && fase !== "gioco") setFotoRisata({ io: fotogramma(videoIo.current, true), lui: fotogramma(videoLui.current, false) });
    else if (fase === "conto" || fase === "attesa") setFotoRisata(null);
  }, [fase]);
  const fotoPerdente =
    esito && !esito.annullata && esito.perdente && fotoRisata ? (esito.perdente === io ? fotoRisata.io : fotoRisata.lui) : null;


  const tempoGiocato = v ? v.tempoGiocatoMs + (v.orologioDa !== null ? Math.max(0, ora - v.orologioDa) : 0) : 0;

  // --- filtri che cambiano da soli durante il gioco ------------------------------------------------
  const rotazioneS = leggiImpostazione(IMP_APP.filtriRotazioneSecondi, 0, eNumero);
  const avvisoS = leggiImpostazione(IMP_APP.filtriRotazioneAvvisoSecondi, 3, eNumero);
  const rotazioneAttiva = rotazioneS > 0 && filtri.stato === "pronto" && filtri.usabili.length > 0;
  const momento = fase === "gioco" && rotazioneAttiva ? momentoRotazione(tempoGiocato, rotazioneS, avvisoS) : null;
  const indiceRotazione = momento?.indice ?? 0;
  const ultimaRotazione = useRef<string | null>(null);
  useEffect(() => {
    if (!v || indiceRotazione === 0) return;
    // Un cambio per indice e per round (ricaricare la vista non rifà il cambio). tVia distingue un round
    // ripartito da capo con lo stesso numero (es. dopo un'interruzione).
    const chiave = `${v.serie.numero}-${v.round}-${v.tVia ?? 0}-${indiceRotazione}`;
    if (ultimaRotazione.current === chiave) return;
    ultimaRotazione.current = chiave;
    const id = prossimoFiltro(filtri.usabili, filtri.attivo, Math.random);
    if (id) filtri.cambiaPerGioco(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indiceRotazione, v?.round, v?.serie.numero, v?.tVia]);
  // Finito o interrotto il round (risultato, nuovo conto alla rovescia, attesa) si torna al filtro scelto
  // (solo se nel round ci sono stati cambi).
  useEffect(() => {
    if (fase === "gioco" || !ultimaRotazione.current) return;
    ultimaRotazione.current = null;
    filtri.ripristina();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fase]);
  // --- filtro a sorpresa a fine round ---------------------------------------------------------------
  // Se nessuno dei due è abbonato (lo dice il server), dopo il risultato ognuno prova per qualche secondo un
  // filtro Silver che non ha, scelto a caso: l'altro lo vede in video. Poi si torna al filtro scelto.
  const sorpresaS = leggiImpostazione(IMP_APP.sorpresaSecondi, 6, eNumero);
  const sorpresaRitardo = leggiImpostazione(IMP_APP.sorpresaRitardoMs, 1500, eNumero);
  const [sorpresa, setSorpresa] = useState<string | null>(null);
  const sorpresaFatta = useRef<string | null>(null);
  const abbonatoIo = account.tipo === "registrato" && account.io.piano !== "base";
  useEffect(() => {
    if (fase !== "risultato" || !chiaveEsito || !esito || esito.annullata || abbonatoIo || sorpresaS <= 0) return;
    if (sorpresaFatta.current === chiaveEsito || filtri.stato !== "pronto" || filtri.sorprese.length === 0 || !st.token) return;
    sorpresaFatta.current = chiaveEsito;
    let chiuso = false;
    let fine: ReturnType<typeof setTimeout> | undefined;
    const via = setTimeout(() => {
      void nessunoAbbonato(codice, st.token!)
        .then((si) => {
          if (!si || chiuso || faseRif.current !== "risultato") return;
          const id = filtri.sorprese[Math.floor(Math.random() * filtri.sorprese.length)]!;
          filtri.provaSorpresa(id);
          setSorpresa(id);
          fine = setTimeout(() => {
            setSorpresa(null);
            filtri.ripristina();
          }, sorpresaS * 1000);
        })
        .catch(() => undefined); // senza risposta niente sorpresa
    }, sorpresaRitardo);
    return () => {
      chiuso = true;
      clearTimeout(via);
      if (fine) {
        // Rivincita, uscita o pagina chiusa durante la sorpresa: si torna subito al filtro scelto.
        clearTimeout(fine);
        setSorpresa(null);
        filtri.ripristina();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fase, chiaveEsito, filtri.stato, filtri.sorprese.length > 0, abbonatoIo]);

  // --- clip della risata (v008) -----------------------------------------------------------------------------
  const linkClip = account.tipo === "registrato" && account.io.nomeUtente && account.io.linkAttivo ? `${new URL(indirizzoSito()).host}/@${account.io.nomeUtente}` : null;
  const clip = useClip({ vista: v, io, remoto, invio, lingua: i18n.language, link: linkClip, nomeLui });
  // v045: «Video per noi» (solo maggiorenni, impostazione accesa): il sì dato una volta si manda da solo a ogni partita.
  // Se lo avete detto tutti e due, a fine round il telefono di chi ha creato la sfida prepara il video (con tutti e due
  // i volti) e lo lascia a Laughdown; il server ricontrolla i due sì. Niente nelle partite contro il bot.
  const noiPossibile = clip.disponibile && !st.prova && noiPossibileQui();
  const noiMandato = useRef(false);
  useEffect(() => {
    if (!noiPossibile || !v || noiMandato.current) return;
    noiMandato.current = true;
    if (scelteAttuali().noi && !v.noi?.[io]) stanza.consensoNoi(true);
  }, [noiPossibile, v, io]);
  const noiTutti = noiPossibile && io === "a" && v?.noi?.a === true && v?.noi?.b === true;
  const noiCaricato = useRef<string | null>(null);
  useEffect(() => {
    if (!noiTutti) return;
    if (clip.stato.s === "pronta_da_creare") return clip.crea();
    if (clip.stato.s !== "fatta" || clip.stato.solo || noiCaricato.current === clip.stato.url || !st.token) return;
    noiCaricato.current = clip.stato.url;
    const c = clip.stato.clip;
    // un numero per round (anche nelle rivincite: serie dopo serie), come lo vuole il server
    const round = v ? Math.min(1000, (v.serie.numero - 1) * 10 + v.round) : 0;
    void caricaVideoNoi({ blob: c.blob, tipo: c.tipo, durataMs: c.durataMs, sfida: codice, token: st.token, round, lingua: i18n.language }).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noiTutti, clip.stato, clip.crea, st.token, codice, io, i18n.language]);
  // v040: le foto della partita (con il mio sì, dato una volta in «Le tue scelte»; l'altro c'è solo con il suo sì)
  const fotoMandato = useRef(false);
  useEffect(() => {
    if (!v || fotoMandato.current) return;
    fotoMandato.current = true;
    if (scelteAttuali().foto && !v.foto?.[io]) stanza.consensoFoto(true);
  }, [v, io]);
  const fotoConLui = v?.foto?.[lui] === true;
  const fotoPartita = useFotoPartita({ fase: v?.fase ?? null, camera: flusso, videoLui, mio: v?.foto?.[io] === true, suo: fotoConLui });
  // v037: «partita del giorno»: il telefono ricorda che oggi hai giocato (giorni di fila in Home)
  useEffect(() => {
    if (fase === "risultato" && v?.esito && !v.esito.annullata) registraGiorno();
  }, [fase, v?.esito]);
  const [premi, setPremi] = useState<{ condivisione: number; amico: number } | null>(null);
  const registratoQui = account.tipo === "registrato";
  useEffect(() => {
    if (!registratoQui) return;
    void leggiRegoleStelle()
      .then((r) => setPremi(r.attivi ? { condivisione: r.condivisione ?? 0, amico: r.amicoNuovo } : null))
      .catch(() => undefined);
  }, [registratoQui]);

  // v038b: audio spento da uno dei due = nessuno sente l'altro (il microfono si spegne e il video dell'altro è muto)
  const mioMuto = v?.muti?.[io] === true;
  const suoMuto = v?.muti?.[lui] === true;
  const audioSpento = mioMuto || suoMuto;
  useEffect(() => {
    for (const tr of flusso.getAudioTracks()) tr.enabled = !audioSpento;
  }, [flusso, audioSpento]);
  // all'entrata: chi aveva spento l'audio l'ultima volta lo ritrova spento (una volta per stanza)
  const mutoMandato = useRef(false);
  useEffect(() => {
    if (!v || mutoMandato.current) return;
    mutoMandato.current = true;
    if (!suoni && !v.muti?.[io]) stanza.muto(true);
  }, [v, io, suoni]);

  const etichettaRiquadro = (r: Ruolo): "ha_riso" | "ha_vinto" | null => {
    if (!esito || esito.annullata || !esito.vincitore) return null;
    return esito.perdente === r ? "ha_riso" : esito.vincitore === r ? "ha_vinto" : null;
  };

  return (
    // v042: a fine round solo il riquadro del risultato (i due video si nascondono; con il filtro a sorpresa resta il mio)
    <div className={`partita disposizione-sopra_sotto fase-${fase}${invito ? " partita-invito" : ""}${fase === "risultato" ? " ld-solo-risultato" : ""}`} data-fase={fase} data-sorpresa={sorpresa ? "1" : undefined}>
      <div className="campo-gioco">
        <Riquadro
          classe="riquadro-lui"
          nome={invito ? "" : nomeLui}
          etichetta={etichettaRiquadro(lui)}
          risata={rilevamento === "attivo" && fase === "gioco" ? punteggi.avversario : null}
          fumetto={quasi?.chi === "lui" ? quasi.testo : null}
          avviso={
            fase === "chiusa"
              ? null
              : !presenteLui
                ? v?.nomi[lui]
                  ? t("gioco.in_attesa_di", { nome: nomeLui })
                  : null // l'avversario non è ancora arrivato: il pannello in basso dice di mandare il link
                : statoVideo !== "collegato"
                  ? t(statoVideo === "interrotto" ? "gioco.video_interrotto" : "gioco.video_collegamento")
                  : null
          }
        >
          <video ref={videoLui} autoPlay playsInline muted={audioSpento} className="video" />
          {suoMuto && (
            <span className="ld-muto-badge" role="img" aria-label={t("gioco.muto_lui", { nome: nomeLui })}>
              <IconaAltoparlante acceso={false} dimensione={18} />
            </span>
          )}
          {invito && <PostoAmico />}
          {/* v028: sul video dell'altro, «segnala» in alto a destra e «Ha riso!» in basso */}
          {!invito && !st.prova && (presenteLui || !!v?.nomi[lui]) && (
            <button type="button" className="pulsante-icona pulsante-segnala" aria-label={t("gioco.segnala")} onClick={() => setSegnala(true)}>
              <IconaSegnala />
            </button>
          )}
          {fase === "gioco" && (
            <button type="button" className="pulsante-risata" aria-label={t("gioco.ha_riso_pulsante")} title={t("gioco.ha_riso_pulsante")} onClick={() => {
                tocco("medio");
                stanza.haRiso();
              }}>
              <IconaRisata />
            </button>
          )}
        </Riquadro>
        <Riquadro
          classe="riquadro-io"
          nome={t("gioco.tu")}
          etichetta={etichettaRiquadro(io)}
          risata={rilevamento === "attivo" && fase === "gioco" ? punteggi.io : null}
          fumetto={quasi?.chi === "io" ? quasi.testo : null}
          avviso={!voltoMio && ["conto", "gioco"].includes(fase) ? t("gioco.inquadra") : filtri.errore ? t("gioco.filtro_fermato") : null}
        >
          <video ref={videoIo} autoPlay playsInline muted className="video video-specchio" />
          {mioMuto && (
            <span className="ld-muto-badge" role="img" aria-label={t("gioco.muto_io")}>
              <IconaAltoparlante acceso={false} dimensione={18} />
            </span>
          )}
          {filtri.stato === "pronto" && (
            // v046: pulsante con la scritta «Cambia filtro» (il proprietario: deve essere chiaro che si può cambiare);
            // finché non lo si è mai toccato, pulsa piano
            <button
              type="button"
              className="pulsante-icona pulsante-filtro ld-pillola"
              data-invita={!toccatoFiltro || undefined}
              aria-pressed={filtri.attivo !== null}
              onClick={() => {
                segnaToccato("filtro");
                setSceltaFiltro(true);
              }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M4 9c0-3 3.5-5 8-5s8 2 8 5v2c0 5-3.6 9-8 9s-8-4-8-9z" />
                <circle cx="9" cy="10.5" r="1.4" fill="currentColor" />
                <circle cx="15" cy="10.5" r="1.4" fill="currentColor" />
                <path d="M9 15c1.8 1.4 4.2 1.4 6 0" />
              </svg>
              <span>{t("gioco.cambia_filtro")}</span>
            </button>
          )}
          {voce.disponibile && (
            <button
              type="button"
              className="pulsante-icona pulsante-voce ld-pillola"
              data-invita={(toccatoFiltro && !toccatoVoce) || undefined}
              aria-pressed={voce.voce !== "normale"}
              onClick={() => {
                segnaToccato("voce");
                setSceltaVoce(true);
              }}
            >
              <IconaVoce />
              <span>{t("voce.cambia")}</span>
            </button>
          )}
        </Riquadro>
      </div>

      {/* v046: il telefono faticava con un filtro pesante: ne è stato messo uno leggero, e lo si dice in positivo */}
      {filtri.cambioFluido && (
        <div className="ld-avviso-fluido" role="status">
          {t("gioco.filtro_fluido")}
        </div>
      )}
      {momento?.avviso && (
        <div className="cambio-filtro" role="status" aria-live="assertive">
          <span>{t("gioco.cambio_faccia")}</span>
          <span key={momento.avviso} className="cambio-filtro-numero">
            {momento.avviso}
          </span>
        </div>
      )}

      <canvas ref={tela} className="coriandoli" aria-hidden="true" />

      <header className="partita-testa">
        <button type="button" className="pulsante-icona pulsante-esci" aria-label={t("gioco.esci")} onClick={() => setConferma(true)}>
          <IconaEsci />
        </button>
        <div className="partita-punteggio" aria-live="polite">
          {v && v.serie.tipo === "meglio3" ? (
            <span>
              {t("gioco.meglio3")} · {v.serie.vittorie[io]}–{v.serie.vittorie[lui]}
            </span>
          ) : v && v.round > 0 ? (
            <span>{t("gioco.round", { numero: v.round })}</span>
          ) : null}
          {fase === "gioco" && <span className="partita-tempo">{mmss(tempoGiocato)}</span>}
        </div>
        {/* v038b: audio della partita: spegnendolo non senti l'altro e l'altro non sente te (lo vede dall'icona).
            v040: solo mentre si gioca (a fine partita non serve) */}
        {fase === "risultato" || fase === "chiusa" ? (
          <span className="pulsante-icona-posto" aria-hidden="true" />
        ) : (
        <button
          type="button"
          className="pulsante-icona pulsante-audio"
          aria-label={mioMuto ? t("gioco.audio_accendi") : t("gioco.audio_spegni")}
          aria-pressed={mioMuto}
          onClick={() => {
            const muto = !mioMuto;
            setSuoni(!muto);
            salvaPreferenza(PREFERENZE.suoni, muto ? "no" : "si");
            stanza.muto(muto);
          }}
        >
          <IconaAltoparlante acceso={!mioMuto} />
        </button>
        )}
      </header>

      {st.prova && !st.daSolo && <PannelloProva />}
      {st.daSolo && (fase === "attesa" || fase === "conto") && <p className="nota-centrata prova-da-solo-nota">{t("prova.da_solo_nota")}</p>}
      {diagnostica && (
        <pre className="diagnostica" aria-hidden="true">
          {`fase ${fase} · video ${st.prova ? "bot" : statoVideo}${soloTurn ? " (solo TURN)" : ""}\nrisata io ${punteggi.io.toFixed(2)} · lui ${punteggi.avversario.toFixed(2)}\nfiltro ${filtri.attivo ?? "-"} · rilevamento ${rilevamento}`}
        </pre>
      )}

      <section className="partita-pannello" aria-live="polite">
        {st.spettatori.length > 0 && (
          <p className="nota-centrata spettatori-badge" role="status">
            {t("torneo.spettatori", { n: st.spettatori.length })}
          </p>
        )}
        <Pannello
          v={v}
          io={io}
          nomeLui={nomeLui}
          ora={ora}
          numeroConto={numeroConto}
          codice={codice}
          rilevamentoAssente={rilevamento === "assente"}
          sorpresa={sorpresa}
          fotoRisata={fotoPerdente}
          onNuovaSfida={onNuovaSfida}
          onEsci={onEsci}
          onSegnala={!invito && !st.prova ? () => setSegnala(true) : null}
          videoPronto={videoPronto}
          onApriFine={() => setFineAperta(true)}
        />
      </section>


      <Finestra aperta={sceltaFiltro} titolo={t("gioco.filtro_titolo")} onChiudi={() => setSceltaFiltro(false)}>
        {sceltaFiltro && (
          <SceltaFiltro
            attivo={filtri.attivo}
            onScegli={(id) => {
              filtri.scegli(id);
              setSceltaFiltro(false);
            }}
          />
        )}
      </Finestra>

      <Finestra aperta={sceltaVoce} titolo={t("voce.titolo")} onChiudi={() => setSceltaVoce(false)}>
        {sceltaVoce && (
          <SceltaVoce
            attiva={voce.voce}
            onScegli={(v) => {
              voce.scegli(v);
              setSceltaVoce(false);
            }}
          />
        )}
      </Finestra>

      {domanda && (
        <DomandaScelte
          modo={domanda}
          videoPossibile={clip.disponibile}
          onFine={() => {
            setDomanda(null);
            setFineAperta(true);
          }}
        />
      )}
      {/* v035: «Che partita!»: condividi sui social (con il video, se c'è), manda a un amico, gioca ancora */}
      {v?.esito && (
        <FinestraFinePartita
          aperta={fineAperta && fase === "risultato" && !propostaDaLui}
          onChiudi={() => setFineAperta(false)}
          vinto={v.esito.vincitore === io ? true : v.esito.vincitore ? false : null}
          nomeLui={nomeLui}
          clip={clip}
          foto={fotoPartita}
          fotoConLui={fotoConLui}
          link={account.tipo === "registrato" && account.io.nomeUtente && account.io.linkAttivo ? `${indirizzoSito()}/@${account.io.nomeUtente}` : indirizzoSito()}
          lingua={i18n.language}
          premi={premi}
          onNuovaSfida={onNuovaSfida}
          onRivincita={
            v.collegati[lui] && !v.proposta
              ? () => {
                  setFineAperta(false);
                  stanza.proponi("singola");
                }
              : null
          }
          onTorneo={torneoDellaPartita(codice) ? () => vai(`${PERCORSO_TORNEO}${torneoDellaPartita(codice)}`) : null}
        />
      )}

      <Finestra aperta={vietato} titolo={t("gioco.bloccato_titolo")} onChiudi={onEsci}>
        <p>{t("gioco.bloccato_testo")}</p>
        <div className="azioni-riga">
          <button type="button" className="pulsante pulsante-primario" onClick={onEsci}>
            {t("gioco.torna_home")}
          </button>
        </div>
      </Finestra>
      <Finestra aperta={conferma} titolo={t("gioco.esci")} onChiudi={() => setConferma(false)}>
        <p>{torneoDellaPartita(codice) ? t("torneo.esci_conferma") : t("gioco.esci_conferma")}</p>
        <div className="azioni-riga">
          <button type="button" className="pulsante pulsante-secondario" onClick={() => setConferma(false)}>
            {t("generale.annulla")}
          </button>
          <button type="button" className="pulsante pulsante-pericolo" onClick={onEsci}>
            {t("gioco.esci")}
          </button>
        </div>
      </Finestra>

      {tutorial && <Tutorial tipo={tipoTutorial} onFine={() => setTutorial(false)} />}
      <Finestra aperta={segnala} titolo={t("gioco.segnala_titolo", { nome: nomeLui })} onChiudi={() => setSegnala(false)}>
        <Segnala nome={nomeLui} codice={codice} token={st.token ?? ""} onFatto={onEsci} onAnnulla={() => setSegnala(false)} />
      </Finestra>
    </div>
  );
}

function Riquadro(p: {
  classe: string;
  nome: string;
  etichetta: "ha_riso" | "ha_vinto" | null;
  risata: number | null;
  avviso: string | null;
  /** v046: un fumetto che compare quando la risata è vicina («Ci sei quasi!», «Resisti!») */
  fumetto?: string | null;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <figure className={`riquadro ${p.classe}`} data-esito={p.etichetta ?? undefined}>
      {p.children}
      {p.fumetto && (
        <span className="ld-quasi" key={p.fumetto} aria-hidden="true">
          {p.fumetto}
        </span>
      )}
      {p.nome && <figcaption className="riquadro-nome">{p.nome}</figcaption>}
      {p.risata !== null && (
        <span className="risatometro" aria-hidden="true">
          <span style={{ blockSize: `${Math.round(Math.min(1, p.risata) * 100)}%` }} />
        </span>
      )}
      {p.avviso && <span className="riquadro-avviso">{p.avviso}</span>}
      {p.etichetta && <span className={`riquadro-esito esito-${p.etichetta}`}>{t(`gioco.${p.etichetta}`)}</span>}
    </figure>
  );
}

interface PropsPannello {
  v: VistaPartita | null;
  io: Ruolo;
  nomeLui: string;
  ora: number;
  numeroConto: number;
  codice: string;
  rilevamentoAssente: boolean;
  /** id del filtro a sorpresa in corso (null = nessuno) */
  sorpresa: string | null;
  /** v020: foto (data URL) di chi ha riso, presa sul telefono quando il gioco si è fermato */
  fotoRisata: string | null;
  onNuovaSfida: () => void;
  onEsci: () => void;
  /** v042: «Segnala» sotto il risultato (i riquadri dei video lì non ci sono più); null = non si segnala (prova) */
  onSegnala: (() => void) | null;
  /** v035: i video sono collegati (si parte da soli) */
  videoPronto: boolean;
  /** v035: riapre «Che partita!» */
  onApriFine: () => void;
}

/** La parte in basso: cambia con la fase della partita. */
function Pannello({ v, io, nomeLui, ora, numeroConto, codice, rilevamentoAssente, sorpresa, fotoRisata, onNuovaSfida, onEsci, onSegnala, videoPronto, onApriFine }: PropsPannello) {
  const { t } = useTranslation();
  const lui = altro(io);
  const link = useMemo(() => linkSfida(codice), [codice]);
  if (!v) return <p className="pannello-testo">{t("generale.caricamento")}</p>;

  switch (v.fase) {
    case "attesa":
      if (!v.collegati[lui] && !v.nomi[lui]) {
        // Sfida mandata dal link personale di qualcuno: si aspetta che accetti lui (il link non serve a nessun altro).
        const per = sfidaPer(codice);
        if (per) return <AttesaRichiesta codice={codice} per={per} onEsci={onEsci} />;
        return (
          <div className="pannello-attesa">
            <InvitoSfida link={link} nome={v.nomi[io] ?? ""} />
            <BotInveceDiAspettare nome={v.nomi[io] ?? ""} />
            {/* v045: mentre si aspetta l'altro, per chi è in Base */}
            <Annuncio posto="attesa" codice={codice} token={stanza.stato.token} />
          </div>
        );
      }
      // v042: appena vi vedete si parte (il proprietario: «una pagina di intermezzo senza utilizzo»): niente scelte da
      // rileggere (stanno nel profilo) né «Aspetta un attimo», solo una riga finché parte il conto alla rovescia
      return (
        <div className="pannello-pronti ld-pronti-breve">
          {rilevamentoAssente && <p className="nota">{t("gioco.rilevamento_assente")}</p>}
          {!v.collegati[lui] ? (
            <>
              <Tavolino codice={codice} nomeLui={nomeLui} />
              <p className="pannello-titolo">{t("gioco.in_attesa_di", { nome: nomeLui })}</p>
            </>
          ) : (
            <p className="pannello-titolo ld-si-parte">{videoPronto && !v.pronti[lui] && v.pronti[io] ? t("gioco.aspetta_altro", { nome: nomeLui }) : videoPronto ? t("gioco.si_parte") : t("gioco.vi_collegate")}</p>
          )}
        </div>
      );

    case "conto":
      // v035: il conto alla rovescia grande in mezzo allo schermo, sopra i video
      return (
        <div className="conto ld-conto-grande" role="timer">
          <span key={numeroConto} className="conto-numero" data-n={numeroConto}>
            {numeroConto > 0 ? numeroConto : t("gioco.via")}
          </span>
          <span className="ld-conto-nota">{t("gioco.non_ridere")}</span>
        </div>
      );

    case "gioco":
      return (
        <div className="pannello-gioco">
          <p className="pannello-testo">{t("gruppo.gioco_istruzioni")}</p>
        </div>
      );

    case "verifica":
      return <p className="pannello-titolo lampo">{t("gioco.risata")}</p>;

    case "accusa": {
      const a = v.accusa!;
      const restano = secondi(a.scade - ora);
      if (a.contro === io) {
        return (
          <div className="pannello-accusa" role="alertdialog" aria-labelledby="accusa-titolo">
            <p id="accusa-titolo" className="pannello-titolo">
              {t("gioco.accusa_tu")}
            </p>
            <p className="pannello-testo">{a.origine === "avversario" ? t("gioco.accusa_tu_testo", { nome: nomeLui }) : t("gioco.accusa_analisi")}</p>
            <div className="azioni-riga">
              <button type="button" className="pulsante pulsante-pericolo" onClick={() => stanza.rispondi(true)}>
                {t("gioco.si_ho_riso")}
              </button>
              <button type="button" className="pulsante pulsante-secondario" onClick={() => stanza.rispondi(false)}>
                {t("gioco.no_non_ho_riso")} ({restano})
              </button>
            </div>
          </div>
        );
      }
      return <p className="pannello-titolo">{t("gioco.accusa_altro", { nome: nomeLui })}</p>;
    }

    case "pausa": {
      const pa = v.pausa!;
      const testo =
        pa.motivo === "scollegato"
          ? t("gioco.pausa_scollegato", { nome: nomeLui })
          : pa.chi === io
            ? t("gioco.pausa_volto_io")
            : t("gioco.pausa_volto", { nome: nomeLui });
      return (
        <div className="pannello-pausa">
          <p className="pannello-titolo">{testo}</p>
          <p className="nota-centrata">{t("gioco.pausa_scade", { secondi: secondi(pa.scade - ora) })}</p>
        </div>
      );
    }

    case "risultato":
      return (
        <PannelloRisultato v={v} io={io} nomeLui={nomeLui} link={link} codice={codice} sorpresa={sorpresa} fotoRisata={fotoRisata} onNuovaSfida={onNuovaSfida} onApriFine={onApriFine} onSegnala={onSegnala} />
      );

    case "chiusa": {
      const torneo = torneoDellaPartita(codice);
      return (
        <div className="pannello-risultato">
          <p className="pannello-titolo">{t("gioco.uscito", { nome: nomeLui })}</p>
          <div className="azioni-colonna">
            {torneo ? (
              <button type="button" className="pulsante pulsante-primario" onClick={() => vai(`${PERCORSO_TORNEO}${torneo}`)}>
                {t("torneo.torna")}
              </button>
            ) : (
              <button type="button" className="pulsante pulsante-primario" onClick={onNuovaSfida}>
                {t("gioco.nuova_sfida")}
              </button>
            )}
            <button type="button" className="pulsante pulsante-secondario" onClick={onEsci}>
              {t("gioco.torna_home")}
            </button>
          </div>
        </div>
      );
    }
  }
}

function PannelloRisultato(p: {
  v: VistaPartita;
  io: Ruolo;
  nomeLui: string;
  link: string;
  codice: string;
  sorpresa: string | null;
  fotoRisata: string | null;
  onNuovaSfida: () => void;
  onApriFine: () => void;
  onSegnala: (() => void) | null;
}) {
  const { v, io, nomeLui, codice, sorpresa, fotoRisata, onNuovaSfida, onApriFine, onSegnala } = p;
  const { t } = useTranslation();
  const e = v.esito!;
  const lui = altro(io);
  let titolo: string;
  if (e.annullata) titolo = t(`gioco.annullata_${e.motivo}`, { defaultValue: t("gioco.annullata_uscita") });
  else if (!e.vincitore) titolo = t("gioco.pareggio");
  else if (e.motivo === "tecnica_volto") titolo = e.vincitore === io ? t("gioco.tecnica_volto_vinto", { nome: nomeLui }) : t("gioco.tecnica_volto_perso");
  else titolo = e.vincitore === io ? t("gioco.hai_vinto") : t("gioco.hai_perso");

  const proposta = v.proposta;
  // Torneo: il primo vincitore decide l'incontro; senza vincitore (pari, annullata) si rigioca.
  const torneo = torneoDellaPartita(codice);
  const decisa = torneo !== null && e.serieFinita && !e.annullata && e.vincitore !== null;
  const tornaTorneo = torneo && (
    <button type="button" className="pulsante pulsante-primario pulsante-grande" onClick={() => vai(`${PERCORSO_TORNEO}${torneo}`)}>
      {t("torneo.torna")}
    </button>
  );
  return (
    <div className="pannello-risultato">
      {fotoRisata && e.vincitore && (
        <figure className="ld-foto-risata" data-chi={e.perdente === io ? "io" : "lui"}>
          <img src={fotoRisata} alt="" />
          <figcaption>{e.perdente === io ? t("gioco.foto_risata_tua") : t("gioco.foto_risata_sua", { nome: nomeLui })}</figcaption>
        </figure>
      )}
      <p className={`pannello-titolo risultato-${e.vincitore === io ? "vinto" : e.vincitore === lui ? "perso" : "pari"}`}>{titolo}</p>
      {/* v033: il riepilogo della partita (tempo, round, serie) */}
      {!e.annullata && <Riepilogo v={v} io={io} />}
      {/* Filtro a sorpresa: qui nel pannello, così non copre i video (si vede la propria faccia nuova). */}
      {sorpresa && (
        <div className="sorpresa-filtro" role="status" aria-live="polite" data-filtro={sorpresa}>
          <strong>{t("gioco.sorpresa_titolo")}</strong>
          <span>{t("gioco.sorpresa_testo")}</span>
          <button type="button" className="pulsante pulsante-piccolo" onClick={() => vai(PERCORSO_PIANI)}>
            {t("gioco.sorpresa_silver")}
          </button>
        </div>
      )}
      {/* v042: «Condividi video e foto» è il primo pulsante, quello «importante» (riapre la pagina con video e foto) */}
      <button type="button" className="ld-cta ld-cta-centrata ld-condividi-fine" onClick={onApriFine}>
        <svg viewBox="0 0 24 24" aria-hidden="true" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
          <circle cx="18" cy="5" r="3" />
          <circle cx="6" cy="12" r="3" />
          <circle cx="18" cy="19" r="3" />
          <path d="M8.6 10.6l6.8-4M8.6 13.4l6.8 4" />
        </svg>
        {t("fine.condividi_tutto")}
      </button>
      {v.serie.tipo === "meglio3" && (
        <p className="nota-centrata">
          {t("gioco.meglio3")} · {v.serie.vittorie[io]}–{v.serie.vittorie[lui]}
        </p>
      )}
      {torneo && !decisa && e.serieFinita && <p className="nota-centrata">{t("torneo.rigiocate")}</p>}
      {decisa ? (
        tornaTorneo
      ) : !e.serieFinita ? (
        <button
          type="button"
          className={`pulsante pulsante-primario pulsante-grande ${v.pronti[io] ? "pulsante-fatto" : ""}`}
          aria-pressed={v.pronti[io]}
          onClick={() => {
            sbloccaAudio();
            stanza.pronto(!v.pronti[io]);
          }}
        >
          {v.pronti[io] ? t("gioco.pronto_fatto") : t("gioco.prossimo_round")}
        </button>
      ) : proposta && proposta.da === lui ? (
        <div className="proposta" role="alertdialog" aria-labelledby="proposta-titolo">
          <p id="proposta-titolo">{t(proposta.tipo === "meglio3" ? "gioco.proposta_meglio3" : "gioco.proposta_rivincita", { nome: nomeLui })}</p>
          <div className="azioni-riga">
            <button type="button" className="pulsante pulsante-primario" onClick={() => stanza.accetta()}>
              {t("gioco.accetta")}
            </button>
            <button type="button" className="pulsante pulsante-secondario" onClick={() => stanza.rifiuta()}>
              {t("gioco.rifiuta")}
            </button>
          </div>
        </div>
      ) : proposta && proposta.da === io ? (
        <p className="nota-centrata">{t("gioco.proposta_inviata", { nome: nomeLui })}</p>
      ) : !v.collegati[lui] ? (
        // v040: niente pulsanti che non si possono premere: se l'altro non c'è più lo si dice e si propone altro
        <div className="azioni-colonna ld-fine-scelta">
          <p className="nota-centrata" role="status">{t("fine.altro_via", { nome: nomeLui })}</p>
          <button type="button" className="pulsante pulsante-primario pulsante-grande" onClick={onNuovaSfida}>
            {t("fine.altra_persona")}
          </button>
        </div>
      ) : (
        // v040: subito le due scelte (prima: «Continua a giocare» e poi la scelta: un tocco in più)
        <div className="azioni-colonna ld-fine-scelta">
          <button type="button" className="pulsante pulsante-secondario pulsante-grande" onClick={() => stanza.proponi("singola")}>
            {t("fine.rivincita")}
          </button>
          <button type="button" className="pulsante pulsante-secondario pulsante-grande" onClick={() => stanza.proponi("meglio3")}>
            {t("gioco.proponi_meglio3")}
          </button>
        </div>
      )}
      {/* v033: aggiungere agli amici l'altro giocatore (se tutti e due avete l'account) */}
      {!torneo && <AmiciziaFinePartita codice={codice} nome={nomeLui} />}
      <AnnuncioRisultato codice={codice} token={stanza.stato.token} />
      {onSegnala && (
        <button type="button" className="pulsante-link ld-segnala-fine" onClick={onSegnala}>
          {t("gioco.segnala")}
        </button>
      )}
    </div>
  );
}

/**
 * Torneo: l'avversario non è mai entrato nella partita. Dopo tornei.assenza_minuti si vince a tavolino (lo decide il
 * server: qui si mostra da che ora e si chiede).
 */
const TAVOLINO_CONTROLLO_MS = 30_000;

function Tavolino({ codice, nomeLui }: { codice: string; nomeLui: string }) {
  const { t } = useTranslation();
  const torneo = torneoDellaPartita(codice);
  const token = torneo ? tokenTorneo(torneo) : null;
  const [dal, setDal] = useState<string | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [invio, setInvio] = useState(false);
  useEffect(() => {
    if (!torneo || !token) return;
    let fermo = false;
    const leggi = () =>
      statoTorneo(torneo, token)
        .then((v) => !fermo && setDal(v.mia?.codice === codice ? v.mia.tavolinoDal : null))
        .catch(() => undefined);
    void leggi();
    const id = setInterval(() => void leggi(), TAVOLINO_CONTROLLO_MS);
    return () => {
      fermo = true;
      clearInterval(id);
    };
  }, [torneo, token, codice]);
  if (!torneo || !token || !dal) return null;
  const ora = new Date(dal).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return (
    <div className="tavolino">
      <p className="nota-centrata">{t("torneo.tavolino_nota", { nome: nomeLui, ora })}</p>
      <button
        type="button"
        className="pulsante pulsante-secondario"
        disabled={invio}
        onClick={() => {
          setInvio(true);
          setErrore(null);
          vinciATavolino(torneo, token)
            .then(() => vai(`${PERCORSO_TORNEO}${torneo}`))
            .catch((e: unknown) => {
              setErrore(e instanceof ErroreRete && e.codice === "troppo_presto" ? "troppo_presto" : "generico");
              setInvio(false);
            });
        }}
      >
        {t("torneo.tavolino")}
      </button>
      {errore && (
        <p className="admin-errore" role="alert">
          {t(`torneo.errore_${errore}`)}
        </p>
      )}
    </div>
  );
}

/** Un fotogramma del video come immagine JPEG piccola (data URL), o null se il video non c'è ancora. */
function fotogramma(v: HTMLVideoElement | null, specchio: boolean): string | null {
  if (!v || v.readyState < 2 || !v.videoWidth) return null;
  try {
    const lato = 360;
    const k = Math.min(1, lato / Math.max(v.videoWidth, v.videoHeight));
    const c = document.createElement("canvas");
    c.width = Math.round(v.videoWidth * k);
    c.height = Math.round(v.videoHeight * k);
    const x = c.getContext("2d");
    if (!x) return null;
    if (specchio) {
      x.translate(c.width, 0);
      x.scale(-1, 1);
    }
    x.drawImage(v, 0, 0, c.width, c.height);
    return c.toDataURL("image/jpeg", 0.82);
  } catch {
    return null;
  }
}

/**
 * v040: la sfida mandata dal link personale di qualcuno aspetta che accetti. Se rifiuta (anche bloccando) o la sfida
 * scade, la sfida non c'è più: si dice subito e si esce (prima si restava ad aspettare per sempre).
 */
function AttesaRichiesta({ codice, per, onEsci }: { codice: string; per: string; onEsci: () => void }) {
  const { t } = useTranslation();
  const [finita, setFinita] = useState(false);
  useEffect(() => {
    let vivo = true;
    const controlla = () =>
      leggiSfida(codice).catch((e: unknown) => {
        if (vivo && e instanceof ErroreRete && e.stato === 404) setFinita(true);
      });
    const id = setInterval(controlla, 4000);
    return () => {
      vivo = false;
      clearInterval(id);
    };
  }, [codice]);
  if (finita)
    return (
      <div className="pannello-attesa" data-attesa="rifiutata" role="status">
        <p className="pannello-titolo">{t("social.richiesta_rifiutata", { nome: per })}</p>
        <button type="button" className="pulsante pulsante-primario pulsante-grande" onClick={onEsci}>
          {t("gioco.torna_home")}
        </button>
      </div>
    );
  return (
    <div className="pannello-attesa" data-attesa="richiesta">
      <p className="pannello-titolo">{t("social.attesa_richiesta", { nome: per })}</p>
      <p className="nota">{t("social.attesa_richiesta_nota")}</p>
    </div>
  );
}

/** v041: il tester non aspetta un avversario: un tocco e gioca subito contro un bot (partita di prova nuova). */
function BotInveceDiAspettare({ nome }: { nome: string }) {
  const account = useAccount();
  const [invio, setInvio] = useState(false);
  if (account.tipo !== "registrato" || account.io.tester !== true) return null;
  return (
    <PulsanteBot
      tipo="gioca"
      disabled={invio}
      onClick={() => {
        setInvio(true);
        creaPartitaProva(nome || account.io.nome)
          .then((r) => {
            salvaToken(r.codice, r.token);
            vai(`${PERCORSO_SFIDA}${r.codice}`);
          })
          .catch(() => setInvio(false));
      }}
    />
  );
}
